import { LANDING_ASSETS } from "../constants/landing.constants";
import { normalizeClassThumbnailUrl } from "../utils/landingImage.utils";
import { slugifyCatalogSegment } from "@/utils/catalogSlug";
import type {
  ClassListItemApi,
  ClassListSnapshot,
  ClassViewDataApi,
  OurClassApiItem,
} from "./landingClass.types";
import type { LandingCategory, LandingCourse, LandingLevel } from "../types/landing.types";
import { LANDING_ENROLL_STEPS } from "../constants/landing.constants";

const CATEGORY_ICON_MAP: Record<string, string> = {
  Vocals: LANDING_ASSETS.veenaHero,
  Instrumental: LANDING_ASSETS.veenaSmall,
  "Playback Series": LANDING_ASSETS.heroGirlTanpura,
  "Music For Children": LANDING_ASSETS.heroGirlTanpura,
  Professional: LANDING_ASSETS.veenaHero,
  "Hobby/ Educational": LANDING_ASSETS.veenaSmall,
};

function resolveImage(
  primary?: string | null,
  fallback?: string
): string {
  if (primary && primary.trim()) {
    return primary;
  }
  return fallback ?? LANDING_ASSETS.levelThumbnail;
}

export function getCategoryIconFallback(name: string): string {
  return CATEGORY_ICON_MAP[name] ?? LANDING_ASSETS.veenaHero;
}

export function mapOurClassToCategory(item: OurClassApiItem): LandingCategory {
  const icon = resolveImage(
    item.icon ?? item.thumbnail,
    getCategoryIconFallback(item.name)
  );

  const levels: LandingLevel[] = item.our_levels.map((level) => ({
    id: level.id,
    title: level.name,
    thumbnail: resolveImage(
      level.thumbnail ?? level.icon,
      LANDING_ASSETS.levelThumbnail
    ),
    categorySlug: slugifyCatalogSegment(level.name) || level.id,
  }));

  return {
    id: item.id,
    title: item.name,
    description: item.description,
    icon,
    levels,
  };
}

export function mapListItemToCourse(
  item: ClassListItemApi,
  levelId: string,
  categorySlug?: string
): LandingCourse {
  const price =
    item.price != null && item.price !== ""
      ? parseFloat(String(item.price))
      : 0;

  const resolvedCategorySlug = categorySlug?.trim() || undefined;

  return {
    id: item.id,
    levelId,
    slug: slugifyCatalogSegment(item.name) || item.id,
    categorySlug: resolvedCategorySlug,
    title: item.name,
    shortDescription: item.description,
    longDescription: item.description,
    thumbnail:
      normalizeClassThumbnailUrl(item.thumbnail_image) ||
      LANDING_ASSETS.courseViolin,
    audienceLabel: item.badge,
    ageLabel: item.badge,
    durationDays: item.validity_in_days,
    units: item.unit_count,
    videos: item.total_videos,
    price,
    createdAt: item.created_at,
    taxAmount: 0,
    benefits: [],
    enrollSteps: LANDING_ENROLL_STEPS,
  };
}

export function mapViewToCourse(
  data: ClassViewDataApi,
  levelId: string,
  snapshot?: ClassListSnapshot | null,
  catalogSlugs?: { categorySlug?: string; courseSlug?: string }
): LandingCourse {
  const price = parseFloat(data.price) || 0;
  const taxPercentage = parseFloat(data.tax_percentage) || 0;
  const totalPrice = parseFloat(data.total_price) || 0;
  const apiThumbnail = normalizeClassThumbnailUrl(
    data.thumbnail_upload?.preview_url
  );
  const thumbnail =
    (snapshot?.thumbnail ?? apiThumbnail) || LANDING_ASSETS.courseViolin;

  const benefits = [...(data.benefits ?? [])]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((b) => ({
      title: b.title,
      description: b.description,
    }));

  return {
    id: data.id,
    levelId,
    slug:
      catalogSlugs?.courseSlug ??
      (slugifyCatalogSegment(data.class_name) || data.id),
    categorySlug: catalogSlugs?.categorySlug,
    schoolId: data.school_id ?? data.school_details?.id,
    title: data.class_name,
    shortDescription: snapshot?.shortDescription ?? data.description,
    longDescription: data.description,
    thumbnail,
    audienceLabel: snapshot?.badge,
    ageLabel: snapshot?.badge ?? "",
    durationDays: data.validity_in_days,
    units: snapshot?.units ?? 0,
    videos: snapshot?.videos ?? 0,
    price,
    taxPercentage,
    totalPrice,
    taxAmount: totalPrice - price,
    benefits,
    enrollSteps: LANDING_ENROLL_STEPS,
  };
}

export function listItemToSnapshot(item: ClassListItemApi): ClassListSnapshot {
  return {
    units: item.unit_count,
    videos: item.total_videos,
    badge: item.badge,
    thumbnail:
      normalizeClassThumbnailUrl(item.thumbnail_image) || undefined,
    shortDescription: item.description,
  };
}

export function courseToListSnapshot(course: LandingCourse): ClassListSnapshot {
  return {
    units: course.units,
    videos: course.videos,
    badge: course.audienceLabel ?? course.ageLabel,
    thumbnail: course.thumbnail,
    shortDescription: course.shortDescription,
  };
}

